import { useCallback, useRef } from 'react';

// Sticky page footers (.sticky-actions: New Inspection, area, closeout, report, import, pool
// selection) overlay the bottom of the document's scroll area. When a field near the bottom takes
// focus — above all with the soft keyboard open, which shrinks the viewport so the footer sits right
// above the keys — the browser scrolls the field to the viewport's bottom edge and the footer covers
// its lower half (Android QA lane A06, 2026-09-16, `android-zh-CN-keyboard-form-*.png`). The sheet
// footers already solved this with scroll-padding (F-R3-03, src/ui/components/Sheet.tsx); this hook is
// the page-level twin: while a sticky footer is mounted, the document's scroll-padding-bottom equals
// the footer's clearance, so scrollIntoView / focus scrolling stops above it. Nothing visual changes.
//
// Returned as a callback ref so conditionally rendered footers (the pool selection bar) register and
// unregister with their DOM node.
export function useStickyFooterClearance(): (node: HTMLElement | null) => void {
  const cleanup = useRef<(() => void) | null>(null);
  return useCallback((node: HTMLElement | null) => {
    cleanup.current?.();
    cleanup.current = null;
    if (!node || typeof document === 'undefined') return;
    const root = document.documentElement;
    const apply = () => {
      root.style.scrollPaddingBottom = `${stickyFooterClearance(node, window.innerHeight)}px`;
    };
    apply();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    observer?.observe(node);
    window.addEventListener('resize', apply);
    window.visualViewport?.addEventListener('resize', apply);
    cleanup.current = () => {
      observer?.disconnect();
      window.removeEventListener('resize', apply);
      window.visualViewport?.removeEventListener('resize', apply);
      root.style.scrollPaddingBottom = '';
    };
  }, []);
}

// The clearance is everything from the footer's top edge to the bottom of the viewport when the
// footer is stuck there (this includes the tab bar it sits on), otherwise its own height.
export function stickyFooterClearance(node: HTMLElement, viewportHeight: number): number {
  const rect = node.getBoundingClientRect();
  const stuck = rect.height > 0 && rect.bottom >= viewportHeight - 1;
  return Math.max(0, Math.round(stuck ? viewportHeight - rect.top : node.offsetHeight));
}
